import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { 
  User, 
  Mail, 
  Building2, 
  Phone, 
  Smartphone,
  Calendar,
  MapPin,
  Users,
  Euro,
  FileText,
  Edit2,
  Check,
  X,
  Gamepad2,
  Briefcase,
  Tag,
  Home
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AnimationVariantPicker from "@/components/AnimationVariantPicker";
import AnimationCatalogCombobox from "@/components/AnimationCatalogCombobox";
import { resolveAnimationsToCatalog } from "@/utils/animationCatalog";

const ExtractedDataPanel = ({ data, onUpdate, animations }) => {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [activityItems, setActivityItems] = useState([]);

  // Fetch the Sellsy "Activité client" list once
  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${apiUrl}/sellsy/activity-items`)
      .then(res => setActivityItems(res.data?.items || []))
      .catch(() => setActivityItems([]));
  }, []);

  // Resolve the raw LLM animation names against the live Sellsy catalog. The
  // raw `requested_animations` array can contain a mix of keywords (waiting
  // for variant choice) and full Sellsy references (already resolved). The
  // matching strategy is SUBSTRING on the full reference (case-insensitive)
  // so detecting "Caricaturiste" surfaces every variant whose reference
  // contains the word ("Caricaturiste numérique", "YB - Caricaturiste", etc.).
  const animationSelections = useMemo(() => {
    const raw = data?.requested_animations || [];
    const allVariants = [];
    const refIndex = new Map(); // lower(reference) -> variant
    (animations || []).forEach((g) => {
      (g.variants || []).forEach((v) => {
        allVariants.push(v);
        if (v.reference) refIndex.set(v.reference.toLowerCase(), v);
      });
    });

    return raw.map((entry) => {
      const key = (entry || "").toLowerCase().trim();
      // 1) Already-resolved (entry IS a full Sellsy reference, user picked it)
      const exact = refIndex.get(key);
      if (exact) {
        return {
          base_name: entry,
          sellsy_item_id: exact.sellsy_item_id,
          reference: exact.reference,
          variant_label: exact.variant_label,
          price: exact.price,
        };
      }
      // 2) Substring match — find every variant whose full reference contains
      //    the keyword. If exactly one → auto-resolve. Else leave unresolved.
      const matches = key
        ? allVariants.filter((v) => (v.reference || "").toLowerCase().includes(key))
        : [];
      if (matches.length === 1) {
        const v = matches[0];
        return {
          base_name: entry,
          sellsy_item_id: v.sellsy_item_id,
          reference: v.reference,
          variant_label: v.variant_label,
          price: v.price,
        };
      }
      // Multi-match or no match — keep the raw keyword so the picker can
      // either offer choices or warn "not in catalog".
      return {
        base_name: entry,
        sellsy_item_id: null,
        reference: null,
        variant_label: null,
        price: null,
      };
    });
  }, [data?.requested_animations, animations]);

  // Persist the user's variant pick back to extractedData.requested_animations
  const handleAnimationChange = useCallback((idx, nextSelection) => {
    const raw = data?.requested_animations || [];
    const next = [...raw];
    next[idx] = nextSelection.reference || nextSelection.base_name;
    onUpdate("requested_animations", next);
  }, [data?.requested_animations, onUpdate]);

  const handleAnimationRemove = useCallback((idx) => {
    const raw = data?.requested_animations || [];
    onUpdate(
      "requested_animations",
      raw.filter((_, i) => i !== idx)
    );
  }, [data?.requested_animations, onUpdate]);

  const handleAnimationAdd = useCallback((baseName) => {
    const raw = data?.requested_animations || [];
    if (!baseName) return;
    if (raw.some((e) => (e || "").toLowerCase() === baseName.toLowerCase())) return;
    onUpdate("requested_animations", [...raw, baseName]);
  }, [data?.requested_animations, onUpdate]);

  // Format YYYY-MM-DD to JJ/MM/AAAA for display
  const formatDateFR = (dateStr) => {
    if (!dateStr) return "";
    const isoMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    return dateStr;
  };

  // Date fields edited in JJ-MM-AAAA but stored canonically as YYYY-MM-DD.
  const DATE_FIELDS = ["event_date", "event_end_date"];
  const isoToFR = (s) => {
    if (!s) return "";
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(s);
  };
  const frToISO = (s) => {
    if (!s) return "";
    const m = String(s).trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : String(s);
  };

  // Chateauform clients get an extra "Numéro de bon de commande" event field.
  const isChateauform = (name) =>
    (name || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "")
      .includes("chateauform");

  const startEditing = (field, value) => {
    setEditingField(field);
    setEditValue(DATE_FIELDS.includes(field) ? isoToFR(value) : (value || ""));
  };

  const saveEdit = () => {
    if (editingField) {
      const val = DATE_FIELDS.includes(editingField) ? frToISO(editValue) : editValue;
      onUpdate(editingField, val);
      setEditingField(null);
      setEditValue("");
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  // Contact fields
  const contactFields = [
    { key: "first_name", label: "Prénom", icon: User, value: data.first_name },
    { key: "last_name", label: "Nom", icon: User, value: data.last_name },
    { key: "job_title", label: "Fonction", icon: Briefcase, value: data.job_title },
    { key: "email", label: "Email", icon: Mail, value: data.email },
    { key: "mobile_phone", label: "Portable", icon: Smartphone, value: data.mobile_phone },
    { key: "landline_phone", label: "Fixe", icon: Phone, value: data.landline_phone },
    { key: "client_activity", label: "Activité client", icon: Tag, value: data.client_activity, selectOptions: activityItems },
  ];

  // Company fields
  const companyFields = [
    { key: "company", label: "Société", icon: Building2, value: data.company },
    { key: "client_activity", label: "Activité client", icon: Tag, value: data.client_activity, selectOptions: activityItems },
    { key: "address", label: "Adresse", icon: Home, value: data.address },
    { key: "postal_code", label: "Code postal", icon: MapPin, value: data.postal_code },
    { key: "city", label: "Ville", icon: MapPin, value: data.city },
  ];

  // Event fields
  const eventFields = [
    { key: "event_date", label: "Date début événement", icon: Calendar, value: data.event_date, displayValue: formatDateFR(data.event_date) },
    { key: "event_end_date", label: "Date fin événement", icon: Calendar, value: data.event_end_date, displayValue: formatDateFR(data.event_end_date) },
    { key: "event_horaires", label: "Horaires", icon: Calendar, value: data.event_horaires },
    { key: "event_location", label: "Lieu événement", icon: MapPin, value: data.event_location },
    { key: "participants_count", label: "Participants", icon: Users, value: data.participants_count },
    { key: "budget", label: "Budget", icon: Euro, value: data.budget },
  ];
  if (isChateauform(data.company)) {
    eventFields.push({ key: "purchase_order_number", label: "Numéro de bon de commande", icon: FileText, value: data.purchase_order_number });
  }

  return (
    <div className="space-y-3" data-testid="extracted-data-panel">
      {/* Contact Info */}
      <div className="section-header">Contact</div>
      <div className="card">
        {contactFields.map(({ key, label, icon: Icon, value, selectOptions }) => (
          <DataRow
            key={key}
            fieldKey={key}
            label={label}
            icon={Icon}
            value={value}
            selectOptions={selectOptions}
            isEditing={editingField === key}
            editValue={editValue}
            onStartEdit={() => startEditing(key, value)}
            onEditChange={setEditValue}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onValueChange={onUpdate}
          />
        ))}
      </div>

      {/* Company Info */}
      <div className="section-header">Société / Client</div>
      <div className="card">
        {companyFields.map(({ key, label, icon: Icon, value, selectOptions }) => (
          <DataRow
            key={key}
            fieldKey={key}
            label={label}
            icon={Icon}
            value={value}
            selectOptions={selectOptions}
            isEditing={editingField === key}
            editValue={editValue}
            onStartEdit={() => startEditing(key, value)}
            onEditChange={setEditValue}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onValueChange={onUpdate}
          />
        ))}
      </div>

      {/* Event Info */}
      <div className="section-header">Événement</div>
      <div className="card">
        {eventFields.map(({ key, label, icon: Icon, value, displayValue }) => (
          <DataRow
            key={key}
            fieldKey={key}
            label={label}
            icon={Icon}
            value={value}
            displayValue={displayValue}
            isEditing={editingField === key}
            editValue={editValue}
            onStartEdit={() => startEditing(key, value)}
            onEditChange={setEditValue}
            onSave={saveEdit}
            onCancel={cancelEdit}
          />
        ))}
      </div>

      {/* Requested Animations — always shown so user can add manually if LLM missed any */}
      <div className="section-header">Animations demandées</div>
      <div className="space-y-2">
        <AnimationCatalogCombobox
          animations={animations}
          selectedBaseNames={animationSelections.map((s) => s.base_name)}
          onAdd={handleAnimationAdd}
        />
        {animationSelections.length > 0 && (
          <AnimationVariantPicker
            selections={animationSelections}
            animations={animations}
            onChange={handleAnimationChange}
            onRemove={handleAnimationRemove}
          />
        )}
      </div>

      {/* Request Summary */}
      {data.request_summary && (
        <>
          <div className="section-header">Résumé de la demande</div>
          <div className="card">
            <p className="text-xs text-slate-600 leading-relaxed" data-testid="request-summary">
              {data.request_summary}
            </p>
          </div>
        </>
      )}

      {/* OCR debug — text GPT Vision extracted from the signature image */}
      {data.signature_ocr_text && (
        <>
          <div className="section-header">Texte lu dans l'image de signature</div>
          <div className="card">
            <p className="text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed font-mono" data-testid="signature-ocr-text">
              {data.signature_ocr_text}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const DataRow = ({ 
  fieldKey, 
  label, 
  icon: Icon, 
  value, 
  displayValue,
  selectOptions,
  isEditing, 
  editValue, 
  onStartEdit, 
  onEditChange, 
  onSave, 
  onCancel,
  onValueChange
}) => {
  const shown = displayValue !== undefined && displayValue !== null && displayValue !== "" ? displayValue : value;
  const hasOptions = Array.isArray(selectOptions) && selectOptions.length > 0;

  // Inline Select for fields with predefined options (e.g. Activité client)
  if (hasOptions) {
    const matched = selectOptions.find(o => o.label?.toLowerCase().trim() === String(value || "").toLowerCase().trim());
    return (
      <div className="data-row" data-testid={`data-row-${fieldKey}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-3 h-3 text-slate-400" />
          <span className="data-label">{label}</span>
        </div>
        <Select value={matched?.label || ""} onValueChange={(v) => onValueChange && onValueChange(fieldKey, v)}>
          <SelectTrigger className="h-7 text-xs w-44" data-testid={`select-${fieldKey}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {selectOptions.map((opt) => (
              <SelectItem key={opt.id} value={opt.label} className="text-xs">{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="data-row group" data-testid={`data-row-${fieldKey}`}>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Icon className="w-3 h-3 text-slate-400" />
        <span className="data-label">{label}</span>
      </div>
      
      {isEditing ? (
        <div className="flex items-center gap-1 flex-1 ml-3 min-w-0">
          <Input
            type="text"
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            className="h-7 text-xs flex-1 min-w-0 px-2"
            autoFocus
            data-testid={`edit-input-${fieldKey}`}
          />
          <button 
            onClick={onSave} 
            className="btn-icon"
            data-testid={`save-edit-${fieldKey}`}
          >
            <Check className="w-3 h-3 text-emerald-600" />
          </button>
          <button 
            onClick={onCancel} 
            className="btn-icon"
            data-testid={`cancel-edit-${fieldKey}`}
          >
            <X className="w-3 h-3 text-slate-400" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className="data-value" data-testid={`data-value-${fieldKey}`}>
            {shown || <span className="text-slate-300">—</span>}
          </span>
          <button 
            onClick={onStartEdit}
            className="btn-icon opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`edit-btn-${fieldKey}`}
          >
            <Edit2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ExtractedDataPanel;
